import { omit } from "lodash";
import { apiSlice } from "../services/apiSlice";
import { transformFilters } from "../utils/transformFilters";
import { access } from "fs";

export const contactApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getContactById: builder.query({
      query: (params) => ({
        url: `/contacts/${params?.id}`,
      }),
    }),

    getPrivateNotesById: builder.query({
      query: (params) => ({
        url: `/notes/${params?.id}`,
        params
      }),
    }),

    addNewNote: builder.mutation({
      query: (data) => ({
        url: `/notes/`,
        method: "POST",
        body: {
          ...data,
        },
      }),
    }),

    updateNote: builder.mutation({
      query: (data) => ({
        url: `/notes/${data?.id}`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
    }),


    deleteNote: builder.mutation({
      query: (id) => ({
        url: `/notes/${id}`,
        method: "DELETE",
      }),
    }),

    updateContact: builder.mutation({
      query: (data) => ({
        url: `/contacts/${data?.id}`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
    }),
    getContacts: builder.query({
      query: ({ page, perPage, sort, query, filters = [], visibleColumns = [], importId }) => {
        const params: Record<string, any> = {
          page,
          perPage,
          query,
        };
        const transformedFilters = transformFilters(filters);
        if (transformedFilters.conditions.length > 0) {
          params.filters = encodeURIComponent(JSON.stringify(transformedFilters));
        }
        if (sort) {
          params.sort = sort.field === 'name' ? 'firstName' : sort.field;
          params.sortDir = sort.direction;
        }

        if (visibleColumns.length > 0) {
          params.listingColumns = visibleColumns
            .filter((column: any) => column !== 'contact_id')
            .join(',');
        }

        if (importId) {
          params.importJobId = importId;
        }

        return {
          url: '/contacts',
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: any) => {

        return { ...response, options: response?.results?.map((item: any) => ({ label: item['First name'] + ' ' + item['Last name'], value: item?.contact_id })) };
      },
      providesTags: ['Contacts'],
    }),


    getDynamicColumns: builder.query({
      query: (scope) => ({
        url: `/search-query/columns`,
        params: { scope },
      }),
    }),

    getDynamicFilters: builder.query({
      query: (scope) => ({
        url: `/search-query/filters`,
        params: { scope },
      }),
    }),

    getMultipleRecords: builder.query({
      query: (recordIds) => ({
        url: `/contacts/multiple?recordIds=${encodeURIComponent(recordIds)}`,
        method: 'GET',
      }),
    }),
    mergeContacts: builder.mutation({
      query: (data) => ({
        url: `/contacts/${data.id}/merge`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
    }),
    documnetPreSignedUrl: builder.mutation({
      query: (data) => ({
        url: `/import/presigned-url`,
        method: "POST",
        body: {
          ...data,
        },
      }),
    }),

    downloadSampleFile: builder.mutation({
      query: (data) => ({
        url: `/import/download-sample`,
        method: "POST",
        body: {
          ...data,
        },
        responseHandler: async (response) => await response.blob(),
      }),
    }),
    createImport: builder.mutation({
      query: (data) => ({
        url: `/import/create`,
        method: "POST",
        body: {
          ...data,
        },
        // responseHandler: async (response) => await response.blob(),
      }),
    }),

    getImportContact: builder.query({
      query: (params) => ({
        url: `/import`,
        method: 'GET',
        params
      }),
      transformResponse: (response: any) => {
        return {
          ...response, // total, page, etc. preserve ho jaye
          data: response.data.map((item: any) => ({
            ...item,
            originalFileName: item?.meta?.originalFileName,
            importType: item?.meta?.importType,
            userName: item?.account?.firstName + ' ' + item?.account?.lastName,
            scope: item?.scope?.displayName,
            errorCount: item?.meta?.errorCount,
            duplicateCount: item?.meta?.duplicateCount,
            totalRecords: item?.meta?.totalRecords,
            totalProcessed: item?.meta?.totalProcessed
          })),
        };
      },
    }),

    getAllAccounts: builder.query({
      query: () => ({
        url: `/accounts`,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        return { ...response, options: response?.contacts?.map((item: any) => ({ label: item?.firstName + ' ' + item?.lastName, value: item?.id })) };
      }
    }),

    // {{ BASE_URL }}/api/import / mappings / 8

    getFieldMapping: builder.query({
      query: (id) => ({
        url: `/import/mappings/${id}`,
        method: 'GET',
      }),
    }),

    getAllFieldsByScopeId: builder.query({
      // query: ({ scopeId, page, search, perPage,importData  }) => ({
      //   url: `/fields?scopeId=${scopeId}&page=${page}&query=${search}&perPage=${perPage}`,
      //   method: 'GET',
      // }),
      query: ({ scopeId, page, search, perPage, importData }) => {
        const params = new URLSearchParams({
          scopeId,
          page: String(page),
          query: search ?? '',
          perPage: String(perPage),
        });

        // ✅ Only add when true
        if (importData === true) {
          params.append('importData', 'true');
        }

        return {
          url: `/fields?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: [{ type: 'fields', id: 'LIST' }],
      transformResponse: (response: any) => {
        return { ...response, options: response?.fields?.map((item: any) => ({ label: item?.name, value: item?.accessKey, dataType: item?.dataType, accessKey: item?.accessKey })) };
      }
    }),


    putFieldMapping: builder.mutation({
      query: (data) => ({
        url: `/import/mappings/${data?.id}`,
        method: 'PUT',
        body: omit(data, ['id'])
      }),
    }),

    importStart: builder.mutation({
      query: (id) => ({
        url: `/import/start/${id}`,
        method: 'PUT',
      }),
    }),

    getImportDatDetail: builder.query({
      query: (id) => ({
        url: `/import/${id}`,
        method: 'GET',
      }),
    }),

    downloadFile: builder.mutation({
      query: ({ id, fileType }) => ({
        url: `/import/post/files/${id}?fileType=${fileType}`,
        method: 'GET',
      }),
    }),

    presignedContactProfile: builder.mutation({
      query: (data) => ({
        url: `/contacts/presigned-url`,
        method: "POST",
        body: {
          ...data,
        },
      }),
    }),

    getAllDynamicColumns: builder.query<any[], { query?: string; scope?: string; isStandard?: boolean }>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        let page = 1;
        const limit = 50;
        let hasMore = true;
        let allResults: any[] = [];

        while (hasMore) {
          const result: any = await fetchWithBQ({
            url: `/search-query/columns`,
            params: {
              query: arg.query || "",
              scope: arg.scope,
              ...(typeof arg.isStandard !== "undefined" && { isStandard: arg.isStandard }),
              page,
              limit,
            },
          });

          if (result.error) return { error: result.error };

          const fields = result.data?.items ?? [];
          allResults = [...allResults, ...fields];

          hasMore = fields.length === limit;
          page++;
        }
        return { data: allResults };
      },
    }),

    deleteImport: builder.mutation({
      query: (id) => ({
        url: `/import/${id}`,
        method: 'DELETE',
      }),
    }),

    getContactAllWidgets: builder.query({
      query: ({ id, scope }) => ({
        url: `/detail-page/${scope}/${id}`,
      }),
    }),

    getLoginHistory: builder.query({
      query: (contactId) => ({
        url: `/contacts/${contactId}/portal-access/login-history`,
        method: 'GET',
      }),
    }),

  }),
});

export const {
  useGetContactByIdQuery,
  useUpdateContactMutation,
  useUpdateNoteMutation,
  useGetPrivateNotesByIdQuery,
  useAddNewNoteMutation,
  useDeleteNoteMutation,
  useGetContactsQuery,
  useLazyGetContactsQuery,
  useGetDynamicColumnsQuery,
  useGetDynamicFiltersQuery,
  useGetMultipleRecordsQuery,
  useMergeContactsMutation,
  useDocumnetPreSignedUrlMutation,
  useDownloadSampleFileMutation,
  useCreateImportMutation,
  useGetImportContactQuery,
  useGetAllAccountsQuery,
  useGetFieldMappingQuery,
  useGetAllFieldsByScopeIdQuery,
  usePutFieldMappingMutation,
  useImportStartMutation,
  useGetImportDatDetailQuery,
  useDownloadFileMutation,
  useLazyGetAllFieldsByScopeIdQuery,
  usePresignedContactProfileMutation,
  useGetAllDynamicColumnsQuery,
  useDeleteImportMutation,
  useGetContactAllWidgetsQuery,
  useGetLoginHistoryQuery
} = contactApiSlice;
